/**
 * Readable locations (#650) — the ONE place that turns a folder path into the
 * words a person reads.
 *
 * On disk an org member's private space is `Persons/<their email>`, and
 * projects and apps carry a `.project` / `.pdapp` ending. The drive sidebar
 * has always called that space "My Space", but every other surface that shows
 * a location (the Save to Drive dialog, search results, the Recent page) used
 * to print the raw names. These helpers change TEXT only: folders, their real
 * names, ids and URLs are untouched.
 *
 * `nameOwnSpace` keeps every crumb (each one is a navigation target) and only
 * renames the viewer's own space — the drive breadcrumb. `readableName` is a
 * single name as a person reads it — the pickers' paths (a pointer keeps its
 * `.chain` / `.shared` ending there, as in the drive breadcrumb).
 *
 * Only the viewer's OWN space is renamed. Another member's stays as their
 * address, which is what makes the two distinguishable. The match requires
 * the parent to be `Persons` as well as the name, so a folder that merely
 * happens to be named after the viewer is left alone.
 */
import { ROOT, stripMiniappFolderExt, stripPointerSuffix, stripProjectFolderExt } from '@papan/types'

export interface LocationCrumb {
  name: string
}

export interface ReadableLocationOptions {
  /** The viewer's identity in this tenant (membership email in an org); null on a personal drive. */
  viewerEmail: string | null | undefined
  /** Localized label for the viewer's own space ("My Space"). */
  mySpaceLabel: string
}

/** Separator between location segments in display text. */
export const LOCATION_SEPARATOR = ' › '

// The viewer's own space is `Persons/<email>` DIRECTLY under the drive root —
// crumbs here start below the root, so it is the second crumb. A copy of that
// shape deeper down (`Archive/Persons/<email>`) is not their space.
function isOwnSpaceAt(crumbs: readonly LocationCrumb[], index: number, viewerEmail: string | null | undefined): boolean {
  const me = viewerEmail?.trim().toLowerCase()
  if (!me || index !== 1) return false
  return crumbs[1].name.toLowerCase() === me && crumbs[0].name === ROOT.PERSONS
}

/** The crumbs (below the drive root) with the viewer's own `Persons/<email>` folder renamed to `mySpaceLabel`. Order and count are preserved. */
export function nameOwnSpace<T extends LocationCrumb>(
  crumbs: readonly T[],
  { viewerEmail, mySpaceLabel }: ReadableLocationOptions,
): T[] {
  return crumbs.map((crumb, i) => (isOwnSpaceAt(crumbs, i, viewerEmail) ? { ...crumb, name: mySpaceLabel } : crumb))
}

/**
 * A folder or item name as a person reads it: no `.project` / `.pdapp` ending — but a
 * `.shared` / `.chain` POINTER keeps its WHOLE name (I112, owner 2026-10-02: a path never
 * hides that it runs through a shortcut — `Library › Work.chain › Super`, as the drive
 * breadcrumb shows it; D27, owner 2026-10-03: a pointer to a project or app keeps the package
 * ending too — `Val.pdapp.chain`, as its row in the same list and the drive show it).
 */
export function readableName(name: string): string {
  if (stripPointerSuffix(name) !== name) return name
  return stripMiniappFolderExt(stripProjectFolderExt(name))
}
